import { useContext, useEffect, useMemo, useReducer, useState } from "react";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { useDialog } from "@datanac/datanac-mui-toolkit/dist/hooks";
import { AddCircleOutline } from "@mui/icons-material";
import DeleteForeverOutlined from "@mui/icons-material/DeleteForeverOutlined";
import EastOutlined from "@mui/icons-material/EastOutlined";
import { Box, Dialog, Grid, Stack, useMediaQuery } from "@mui/material";
import Button from "@mui/material/Button";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { _sessionManager, RmaApiHelper, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import DataFormSelect from "components/Core/DataFormSelect";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { MarketingIcon } from "components/Icons/Icons";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import SkeletonTable from "components/shared/table-skeleton/table-skeleton";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { useSnackbar } from "notistack";
import { formatDate, formatDateContext, formatNumber, formatPrice } from "utility/formatters";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { SalesEditDialog } from "./SalesEditDialog";
import { formatDeliveryPeriod, SALE_CATEGORY_CASH, SALE_CATEGORY_HTA } from "./SalesHelper";

export default function SalesWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });

  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });
  const { addAttachments } = useChatAssistantAttachments();
  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);
  const [currentCategory, setCurrentCategory] = useState("All Categories");

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  // ---

  const handleClickNewSale = async () => {
    setEditingSale({
      producer_token: globalState.producer_token,
      crop_year: currentYear,
      trade_date: new Date(),
    });
  };

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const handleLoadSelectedSale = async (id) => {
    // Refresh from API:
    try {
      // Get Sales:
      const _sales = await UsersApiHelper.users_selectObject("Sale", { id }, globalState);
      if (_sales?.length) {
        setEditingSale(_sales[0]);
      } else {
        console.error("Could not load sale", id);
      }
    } catch (err) {
      setEditingSale(null);
      handleError(err);
    }
  };

  const _loadSales = async () => {
    try {
      // Get Sales:
      const _salesFilter = {
        crop_year: currentYear,
        is_active: true,
      };
      if (filter?.commodity_name) {
        _salesFilter.commodity_name = filter.commodity_name;
      }
      if (filter?.commodity_name_list?.length == 1) {
        _salesFilter.commodity_name = filter.commodity_name_list[0];
      }
      const _sales = await UsersApiHelper.users_selectObject("Sale", _salesFilter, globalState);

      let _filteredSales = _sales;

      if (filter?.commodity_name_list?.length) {
        _filteredSales = _sales.filter((obj) => filter?.commodity_name_list?.includes(obj.commodity_name));
      }

      if (filter?.search_contract_number) {
        _filteredSales = _filteredSales.filter((obj) =>
          obj.contract_number?.toLowerCase().includes(filter?.search_contract_number?.toLowerCase())
        );
      }

      // Order by trade_date desc
      _filteredSales = _filteredSales.sort((a, b) => new Date(b.trade_date) - new Date(a.trade_date));

      // Add dummy fields:
      _filteredSales = _filteredSales.map((s) => {
        if (s?.category == SALE_CATEGORY_CASH || s?.category == SALE_CATEGORY_HTA) {
          s.price_cash = (s?.price || 0) + (s?.basis || 0);
        } else {
          s.price_cash = null;
        }
        return s;
      });

      setSales(_filteredSales);

      setIsLoading(false);
    } catch (err) {
      setSales(null);
      setIsLoading(false);
      handleError(err);
    }
  };

  const [sales, setSales] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (globalState?.producer_token) {
      setIsLoading(true);
      _loadSales();
    }
  }, [currentYear, globalState?.producer_token]);

  useEffect(() => {
    addAttachments([{ name: "Sales", content: sales }]);
  }, [sales]);

  // --- --- ---

  const [editingSale, setEditingSale] = useState(null);

  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();

  const isValid = () => {
    return true;
  };

  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {});

  useEffect(() => {
    setIsLoading(true);
    _loadSales();
  }, [filter]);

  const filterHandleChangeState = (event, value) => {
    if (value == null && event.target.value != null) {
      value = event.target.value;
    }

    dispatchFilter({
      type: "update",
      payload: { key: event.target.name, value: value },
    });
  };

  const handleChange = async (sale) => {
    try {
      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });

      if (!sale.producer_token) {
        sale.producer_token = globalState.producer_token;
      }
      if (!sale.crop_year) {
        sale.crop_year = currentYear;
      }

      await api.callPost("marketing/Sale", sale, globalState);

      setIsLoading(true);
      _loadSales();
    } catch (err) {
      handleError(err);
    }
  };

  const handleDeleteSale = (sale) => {
    confirmDialogActions.confirm({
      title: "Delete this entry?",
      prompt: "Are you sure you want to delete this entry?",
      promptDetail: null,
      callback: async () => {
        await handleChange({
          ...sale,
          is_active: false,
        });
        setEditingSale(null);
      },
    });
  };

  // --

  const columns = useMemo(
    () => [
      {
        field: "trade_date",
        headerName: "Trade Date",
        type: "date",
        minWidth: 150,
        valueFormatter: (value) => formatDate(value),
        flex: 1,
      },
      {
        field: "financial_institution_name",
        headerName: "Buyer",
        minWidth: 200,
        flex: 2,
      },
      {
        field: "commodity_name",
        headerName: "Commodity",
        minWidth: 0,
        flex: 1,
      },
      {
        field: "category",
        headerName: "Category",
        minWidth: 100,
        flex: 1,
      },
      {
        field: "symbol",
        headerName: "Symbol",
        minWidth: 120,
        flex: 1,
      },
      {
        field: "price",
        headerName: "Futures",
        minWidth: 120,
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value !== null && value !== undefined && value !== "" ? formatNumber(value, 4) : ""),
      },
      {
        field: "basis",
        headerName: "Basis",
        type: "number",
        minWidth: 120,
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value !== null && value !== undefined && value !== "" ? formatNumber(value, 4) : ""),
      },
      {
        field: "price_cash",
        headerName: "Cash",
        minWidth: 120,
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value !== null && value !== undefined && value !== "" ? formatNumber(value, 4) : ""),
      },
      {
        field: "amount",
        headerName: "Quantity",
        type: "number",
        minWidth: 0,
        valueFormatter: (value) => formatNumber(value, 0),
        align: "right",
        headerAlign: "right",
        flex: 0,
      },
      {
        field: "delivery_period_start",
        headerName: "Delivery",
        minWidth: 120,
        flex: 2,
        valueFormatter: (value, row) =>
          sales?.length ? formatDeliveryPeriod(sales?.find((s) => s.id == row?.id)) : null,
      },
      {
        field: "contract_number",
        headerName: "Contract",
        minWidth: 0,
        flex: 2,
      },
      {
        field: "revenue",
        headerName: "Revenue",
        minWidth: 120,
        flex: 1,
        renderCell: (params) => (
          <Box>
            <abbr title={formatDateContext(params?.row?.market_value_timestamp)}>
              {formatPrice(params.row?.revenue, 0)}
            </abbr>
          </Box>
        ),
        align: "right",
        headerAlign: "right",
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        sortable: false,
        getActions: (params) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteForeverOutlined />}
            label="Delete Sale"
            onClick={() => handleDeleteSale(params?.row)}
          />,
          <GridActionsCellItem
            key="view-details"
            icon={<EastOutlined />}
            label="View Details"
            onClick={() => handleLoadSelectedSale(params?.row?.id)}
          />,
        ],
        flex: 1,
      },
    ],
    [sales]
  );

  return (
    <Box sx={{ minHeight: "800px" }}>
      <WorkspaceHeader
        title="Sales"
        category={{
          title: "Marketing",
          icon: <MarketingIcon />,
        }}
      />

      <Grid container>
        <Grid item xs={12} lg={12}>
          <WorkspacePageHeader
            currentYear={currentYear}
            onYearChange={setCurrentYear}
            isLoading={isLoading}
            additionalControls={
              <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                <DataFormSelect
                  value={filter.commodity_name_list || []}
                  name="commodity_name_list"
                  isRequired={false}
                  onChange={filterHandleChangeState}
                  objectNameLabel="Commodity"
                  itemGetKey={(item) => item.commodity_name}
                  itemGetLabel={(item) => item.commodity_name}
                  doFetch={currentYear !== null}
                  isLoading={false}
                  multiple={true}
                  limitTags={isSmall ? 2 : 3}
                  autoSelect={false}
                  className="mobile-sync-field"
                  customDataLoader={() => RmaApiHelper.searchCommodity({}, globalState)}
                />
                <StatefulControlledTextField
                  label="Contract #"
                  name="search_contract_number"
                  isRequired={false}
                  defaultValue={filter?.search_contract_number || ""}
                  onChange={filterHandleChangeState}
                  InputLabelProps={{ shrink: true }}
                  className="mobile-sync-field"
                />
              </Stack>
            }
            className="headerWithMenuButton salesWorkspace marketing-position"
            alignTitle="center"
          />
        </Grid>
      </Grid>

      {isLoading ? (
        // Render skeletons if data is loading
        <SkeletonTable skeletonTableRows={10} skeletonTableColumn={10} />
      ) : (
        <div style={{ width: "100%", overflowX: "auto" }}>
          <TilleyDataGrid
            rows={sales || []}
            columns={columns}
            pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
            defaultPageSize={isMobile ? 5 : 10}
            gridKey="sales-workspace-grid"
            getRowId={(row) => row.id}
            isLoading={isLoading}
          />
        </div>
      )}

      <WorkspaceActionBar
        content={
          <>
            <div {...(!isValid() ? { inert: "true" } : {})}>
              <Button variant="contained" onClick={handleClickNewSale} disabled={!isValid()}>
                <AddCircleOutline sx={{ mr: 1 }} />
                Add Sale
              </Button>
            </div>
          </>
        }
      />
      <Dialog open={Boolean(editingSale != null)}>
        <SalesEditDialog
          producer_token={globalState.producer_token} //NOT override.
          onChange={handleChange}
          onClose={() => {
            setEditingSale(null);
            setIsLoading(true);
            _loadSales();
          }}
          onDelete={handleDeleteSale}
          sale={editingSale}
          currentCategory={currentCategory}
          crop_year={currentYear}
        />
      </Dialog>
      {confirmDialogComponent}
    </Box>
  );
}
